import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { appInitializer } from './app.initializer';
import { TranslateModule } from '@ngx-translate/core';
import {
  AlertService,
  ModalService,
  TokenInterceptor,
  AuthenticationService,
  InstanceService,
  MepStorage,
  PlatformService,
  StorageWebService,
  MepFile,
  FileWebService,
  Hub,
  HubWebService,
  GTMService,
  ProfileService,
  Common,
  CommonUrlBasedService,
} from '@meplis/services';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { MatDialogModule } from '@angular/material/dialog';
import {
  DynamicHtmlModule,
  FilepickerInsertComponent,
  FilesVideoComponent,
  SocialExternalLinkComponent,
  SocialHashtagLinkComponent,
  SocialProfileLinkComponent,
} from '@meplis/components';
import { Location } from '@angular/common';
import {
  GoogleTagManagerConfiguration,
  GoogleTagManagerModule,
} from 'angular-google-tag-manager';
import { environment } from '@meplis/environments';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      inMemoryScrollingFeature,
      withEnabledBlockingInitialNavigation()
    ),
    provideAnimations(),
    provideAppInitializer(() => {
      const initializerFn = appInitializer(
        inject(AuthenticationService),
        inject(Common),
        inject(GTMService),
        inject(Hub),
        inject(InstanceService),
        inject(Location)
      );
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    importProvidersFrom(
      AlertService,
      DynamicHtmlModule.forRoot({
        components: [
          {
            component: FilepickerInsertComponent,
            selector: 'filepicker-insert',
          },
          {
            component: SocialProfileLinkComponent,
            selector: 'mec-social-profile-link',
          },
          {
            component: SocialHashtagLinkComponent,
            selector: 'mec-social-hashtag-link',
          },
          {
            component: SocialExternalLinkComponent,
            selector: 'mec-social-external-link',
          },
          {
            component: FilesVideoComponent,
            selector: 'files-video',
          },
        ],
      }),
      FontAwesomeModule,
      GoogleTagManagerModule.forRoot(),
      HttpClientModule,
      MatDialogModule,
      MatSnackBarModule,
      ModalService,
      NgCircleProgressModule.forRoot({
        radius: 60,
        space: -9,
        maxPercent: 100,
        outerStrokeGradient: true,
        showTitle: true,
        showSubtitle: false,
        lazy: false,
      }),
      TranslateModule.forRoot()
    ),
    {
      provide: Common,
      useClass: CommonUrlBasedService,
    },
    {
      provide: MepStorage,
      deps: [PlatformService],
      useClass: StorageWebService,
    },
    {
      provide: MepFile,
      useClass: FileWebService,
    },
    {
      provide: Hub,
      useClass: HubWebService,
    },
  ],
};
